import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';
import {
  SSE_DATA_CHANGE_EVENTS,
  SYNC_TOAST_THROTTLE_MS,
  hasActiveQuery,
  sseInvalidationTargets,
} from './sseInvalidation';

/**
 * Connects to the SSE endpoint and invalidates relevant queries
 * when other users make changes.
 *
 * 데이터 변경 이벤트는 이벤트별 대상 쿼리만 무효화한다(sseInvalidation.ts). TanStack 기본대로 현재 화면에
 * 떠 있는 쿼리만 다시 받아오고, "새로고침되었습니다" 토스트도 그런 쿼리가 있을 때만 5초에 한 번 띄운다 —
 * 스토리보드·회의록처럼 무관한 화면에서는 조용하다.
 */
export function useSSE() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const queryClient = useQueryClient();
  const esRef = useRef<EventSource | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const lastSyncToastAtRef = useRef(0);

  const connect = useCallback(
    (token: string) => {
      // EventSource doesn't support custom headers, so pass token as query param
      const url = `/api/events?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      esRef.current = es;
      return es;
    },
    [],
  );

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    const setupEs = (token: string) => {
      const es = connect(token);

      es.onopen = () => {
        // 연결 성공 시 재시도 카운트 초기화
        retryCountRef.current = 0;
      };

      const handleDataChange = (event: MessageEvent) => {
        let data: Record<string, unknown> = {};
        try {
          const parsed: unknown = JSON.parse(event.data);
          if (parsed && typeof parsed === 'object') data = parsed as Record<string, unknown>;
        } catch {
          // 페이로드가 깨져도 이벤트 종류만으로 목록 쿼리는 무효화한다
        }
        const targets = sseInvalidationTargets(event.type, data);
        if (targets.length === 0) return;

        // 무효화 전에 판정 — 화면에 떠 있는 쿼리가 실제로 다시 받아질 때만 알린다
        const visible = hasActiveQuery(queryClient, targets);
        for (const t of targets) {
          void queryClient.invalidateQueries({ queryKey: t.queryKey, exact: t.exact });
        }
        if (!visible) return;

        const now = Date.now();
        if (now - lastSyncToastAtRef.current < SYNC_TOAST_THROTTLE_MS) return;
        lastSyncToastAtRef.current = now;
        toast.info('다른 사용자가 데이터를 수정하여 새로고침되었습니다.', {
          duration: 3000,
          id: 'sse-sync', // prevent duplicate toasts
        });
      };
      for (const name of SSE_DATA_CHANGE_EVENTS) es.addEventListener(name, handleDataChange);

      // Real-time notification listener
      es.addEventListener('notification:new', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
          queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
          toast.info(data.title, {
            description: data.message,
            duration: 5000,
          });
        } catch {
          // ignore parse errors
        }
      });

      // Real-time notice listener
      es.addEventListener('notice:published', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          queryClient.invalidateQueries({ queryKey: ['notices'] });
          queryClient.invalidateQueries({ queryKey: ['notices', 'unread-count'] });
          toast.info('새 공지사항', {
            description: data.title,
            duration: 5000,
          });
        } catch {
          // ignore parse errors
        }
      });

      // Notice data sync (created/updated/deleted by other users)
      const handleNoticeUpdate = (_event: MessageEvent) => {
        queryClient.invalidateQueries({ queryKey: ['notices'] });
        queryClient.invalidateQueries({ queryKey: ['notices', 'unread-count'] });
      };
      es.addEventListener('notice:created', handleNoticeUpdate);
      es.addEventListener('notice:updated', handleNoticeUpdate);
      es.addEventListener('notice:deleted', handleNoticeUpdate);

      // 화이트보드 참가자·내용·캡처 변화 — 안건 상세의 버튼 표시(점·N명)·캡처 목록만 갱신, 토스트 없음
      es.addEventListener('whiteboard:presence', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          const agendaId = Number(data.agendaId);
          if (agendaId) {
            queryClient.invalidateQueries({ queryKey: ['whiteboard', 'status', agendaId] });
            queryClient.invalidateQueries({ queryKey: ['whiteboard', 'snapshots', agendaId] });
          }
        } catch {
          // ignore parse errors
        }
      });

      es.onerror = () => {
        // 브라우저 EventSource는 CONNECTING 상태로 자동 재연결을 시도함.
        // 단, readyState === CLOSED(2)이면 서버가 영구적으로 연결을 닫은 것(토큰 만료 등)이므로
        // 자동 재연결이 발생하지 않아 지수 백오프로 수동 재연결한다.
        console.warn('[SSE] 연결 오류 발생. readyState:', es.readyState);

        if (es.readyState === EventSource.CLOSED) {
          es.close();
          esRef.current = null;

          const maxDelay = 30_000;
          const delay = Math.min(1_000 * 2 ** retryCountRef.current, maxDelay);
          retryCountRef.current += 1;

          console.warn(`[SSE] 연결이 종료되었습니다. ${delay}ms 후 재연결 시도 (#${retryCountRef.current})`);

          retryTimerRef.current = setTimeout(() => {
            setupEs(token);
          }, delay);
        }
        // readyState === CONNECTING: 브라우저가 자동 재연결 중 — 별도 처리 불필요
      };

      return es;
    };

    const es = setupEs(accessToken);

    return () => {
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      es.close();
      esRef.current = null;
    };
  }, [isAuthenticated, accessToken, queryClient, connect]);
}
